import { useCallback, useEffect, useRef, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import PresenceBar from './components/PresenceBar';
import SessionTimeline from './components/SessionTimeline';
import SessionSidePanel from './components/SessionSidePanel';
import SoftAssignModal from './components/SoftAssignModal';
import { SUPERVISOR_TABS, SESSION_STATUS_LABEL, AUDIT_ACTION_LABEL, formatWaitSeconds, sessionTitle } from './csConstants';
import useCsAdminSse from './useCsAdminSse';
import './csWorkbench.css';

function mergeBySeq(prev, next) {
  const map = new Map();
  [...(prev || []), ...(next || [])].forEach((m) => {
    map.set(m.serverMessageId || `seq-${m.seq}`, m);
  });
  return Array.from(map.values()).sort((a, b) => (a.seq || 0) - (b.seq || 0));
}

function formatEndedAt(item) {
  const raw = item?.endedAt || item?.abandonedAt;
  if (!raw) return '—';
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    return d.toLocaleString();
  } catch {
    return String(raw);
  }
}

function formatAuditTime(raw) {
  if (!raw) return '—';
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    return d.toLocaleString();
  } catch {
    return String(raw);
  }
}

export default function CsSupervisorPage() {
  const [tab, setTab] = useState(SUPERVISOR_TABS.QUEUE);
  const [queueItems, setQueueItems] = useState([]);
  const [endedSessions, setEndedSessions] = useState([]);
  const [agents, setAgents] = useState([]);
  const [listError, setListError] = useState('');
  const [selected, setSelected] = useState(null);
  const [watching, setWatching] = useState(false);
  const [messages, setMessages] = useState([]);
  const [msgMeta, setMsgMeta] = useState({ hasMoreBefore: false, oldestSeq: null, newestSeq: null });
  const [msgError, setMsgError] = useState('');
  const [banner, setBanner] = useState('');
  const [softAssignOpen, setSoftAssignOpen] = useState(false);
  const [auditSummary, setAuditSummary] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditCursor, setAuditCursor] = useState(null);
  const [auditHasMore, setAuditHasMore] = useState(false);
  const [auditFilterAction, setAuditFilterAction] = useState('');
  const [selectedAuditId, setSelectedAuditId] = useState(null);

  const selectedRef = useRef(selected);
  const msgMetaRef = useRef(msgMeta);
  const tabRef = useRef(tab);
  selectedRef.current = selected;
  msgMetaRef.current = msgMeta;
  tabRef.current = tab;

  const loadLists = useCallback(async () => {
    setListError('');
    try {
      const [queue, ended, agentList] = await Promise.all([
        adminApi.imCsSupervisorQueue({ limit: 50 }),
        adminApi.imCsSupervisorEndedSessions({ limit: 50 }).catch(() => ({ items: [] })),
        adminApi.imCsListAgents({ limit: 50 }),
      ]);
      setQueueItems(queue?.items || []);
      setEndedSessions(ended?.items || []);
      setAgents(Array.isArray(agentList) ? agentList : (agentList?.items || []));
    } catch (err) {
      setListError(err.message || '載入主管視圖失敗');
    }
  }, []);

  const loadAuditLogs = useCallback(async ({ append = false, cursor } = {}) => {
    setListError('');
    try {
      const page = await adminApi.imCsSupervisorAuditLogs({
        limit: 40,
        cursor: append ? cursor : undefined,
        action: auditFilterAction || undefined,
      });
      const items = page?.items || [];
      setAuditLogs((prev) => (append ? [...prev, ...items] : items));
      setAuditHasMore(!!page?.hasMore);
      setAuditCursor(page?.nextCursor || null);
    } catch (err) {
      if (!append) setAuditLogs([]);
      setListError(err.message || '載入 Audit 失敗');
    }
  }, [auditFilterAction]);

  const loadAudit = useCallback(async (sessionId) => {
    if (sessionId == null || sessionId === '') {
      setAuditSummary(null);
      return;
    }
    setAuditLoading(true);
    try {
      const summary = await adminApi.imCsAuditSummary(String(sessionId));
      setAuditSummary(summary);
    } catch {
      setAuditSummary(null);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const loadMessages = useCallback(async (sessionId, opts = {}) => {
    if (sessionId == null || sessionId === '') return;
    const sid = String(sessionId);
    setMsgError('');
    try {
      const page = await adminApi.imCsListMessages(sid, { limit: 50, ...opts });
      const items = page?.items || [];
      if (opts.beforeSeq != null) setMessages((prev) => mergeBySeq(items, prev));
      else if (opts.afterSeq != null) setMessages((prev) => mergeBySeq(prev, items));
      else setMessages(items);
      setMsgMeta({
        hasMoreBefore: !!page?.hasMoreBefore,
        oldestSeq: page?.oldestSeq != null ? String(page.oldestSeq) : null,
        newestSeq: page?.newestSeq != null ? String(page.newestSeq) : null,
      });
    } catch (err) {
      setMsgError(err.message || '載入訊息失敗');
    }
  }, []);

  const { connected: sseConnected } = useCsAdminSse({
    onQueueEvent: () => {
      if (tabRef.current === SUPERVISOR_TABS.QUEUE) loadLists();
    },
    onPresenceEvent: () => loadLists(),
    onSessionStatus: () => loadLists(),
    onMessageCreated: (event) => {
      const sid = event?.sessionId != null ? String(event.sessionId) : null;
      const payload = event?.payload;
      const current = selectedRef.current;
      if (!sid || !current?.sessionId || String(current.sessionId) !== sid) return;
      if (payload?.serverMessageId || payload?.seq != null) {
        setMessages((prev) => mergeBySeq(prev, [payload]));
        if (payload.seq != null) {
          setMsgMeta((m) => ({ ...m, newestSeq: String(payload.seq) }));
        }
        return;
      }
      const meta = msgMetaRef.current;
      if (meta.newestSeq != null) loadMessages(current.sessionId, { afterSeq: meta.newestSeq });
      else loadMessages(current.sessionId);
    },
  });

  useEffect(() => {
    loadLists();
    const timer = setInterval(loadLists, sseConnected ? 60_000 : 8000);
    return () => clearInterval(timer);
  }, [loadLists, sseConnected]);

  useEffect(() => {
    if (tab !== SUPERVISOR_TABS.AUDIT) return undefined;
    loadAuditLogs({ append: false });
    return undefined;
  }, [tab, loadAuditLogs]);

  useEffect(() => {
    if (!selected?.sessionId) {
      setMessages([]);
      setWatching(false);
      setAuditSummary(null);
      return undefined;
    }
    loadMessages(selected.sessionId);
    loadAudit(selected.sessionId);
    const ended = selected.status === 'ENDED' || selected.status === 'ABANDONED'
      || tabRef.current === SUPERVISOR_TABS.ENDED
      || tabRef.current === SUPERVISOR_TABS.AUDIT;
    if (ended) {
      return undefined;
    }
    const timer = setInterval(() => {
      if (sseConnected) return;
      setMsgMeta((meta) => {
        if (meta.newestSeq != null) loadMessages(selected.sessionId, { afterSeq: meta.newestSeq });
        else loadMessages(selected.sessionId);
        return meta;
      });
    }, 5000);
    return () => clearInterval(timer);
  }, [selected?.sessionId, selected?.status, loadMessages, loadAudit, sseConnected]);

  async function handleWatch() {
    try {
      await adminApi.imCsWatch(selected.sessionId);
      setWatching(true);
      setBanner('已開始監看（唯讀）');
      loadMessages(selected.sessionId);
    } catch (err) {
      setBanner(err.message || 'Watch 失敗');
    }
  }

  async function handleUnwatch() {
    try {
      await adminApi.imCsUnwatch(selected.sessionId);
      setWatching(false);
      setBanner('已結束監看');
    } catch (err) {
      setBanner(err.message || 'Unwatch 失敗');
    }
  }

  async function pickAgent(promptText) {
    const id = window.prompt(promptText);
    if (!id) return null;
    const trimmed = String(id).trim();
    // Snowflake IDs must stay as strings (JS Number loses precision)
    return /^\d+$/.test(trimmed) ? trimmed : null;
  }

  async function handleForceAssign() {
    const target = await pickAgent('Force Assign 目標 Agent Admin ID');
    if (target == null) return;
    try {
      await adminApi.imCsForceAssign(selected.sessionId, {
        targetAgentAdminId: target,
        overrideCodes: ['OVERRIDE_CAPACITY', 'OVERRIDE_STATUS'],
        reason: 'supervisor-workbench',
      });
      setBanner('Force Assign 已執行');
      loadLists();
    } catch (err) {
      setBanner(err.message || 'Force Assign 失敗');
    }
  }

  async function handleForceRelease() {
    if (!window.confirm('確定 Force Release？')) return;
    try {
      await adminApi.imCsForceRelease(selected.sessionId, { reason: 'supervisor-workbench' });
      setBanner('Force Release 已執行');
      setSelected(null);
      loadLists();
    } catch (err) {
      setBanner(err.message || 'Force Release 失敗');
    }
  }

  async function handleForceTransfer() {
    const target = await pickAgent('Force Transfer 目標 Agent Admin ID');
    if (target == null) return;
    try {
      await adminApi.imCsForceTransfer(selected.sessionId, {
        targetAgentAdminId: target,
        overrideCodes: ['OVERRIDE_CAPACITY', 'OVERRIDE_STATUS'],
        reason: 'supervisor-workbench',
      });
      setBanner('Force Transfer 已執行');
      loadLists();
    } catch (err) {
      setBanner(err.message || 'Force Transfer 失敗');
    }
  }

  function handleSelect(item, nextTab) {
    setTab(nextTab);
    setSelected(item);
    setSelectedAuditId(null);
  }

  function handleSelectAudit(item) {
    setSelectedAuditId(item.id);
    if (item.sessionId != null) {
      setSelected({
        sessionId: item.sessionId,
        conversationId: item.conversationId,
        status: 'ENDED',
      });
    }
  }

  const selectedEnded = selected
    && (selected.status === 'ENDED' || selected.status === 'ABANDONED'
      || tab === SUPERVISOR_TABS.ENDED
      || tab === SUPERVISOR_TABS.AUDIT);

  return (
    <div className="page-card cs-page">
      <div className="section-header">
        <h2>主管中心</h2>
        <span className="cs-hint">{sseConnected ? '即時連線中' : '輪詢模式'}</span>
      </div>
      <PresenceBar />
      {banner ? <div className={banner.includes('失敗') ? 'cs-error' : 'cs-hint'}>{banner}</div> : null}
      {listError ? <div className="cs-error">{listError}</div> : null}
      <div className="cs-supervisor-grid">
        <div className="cs-panel">
          <div className="cs-tabs">
            <button
              type="button"
              className={tab === SUPERVISOR_TABS.QUEUE ? 'is-active' : ''}
              onClick={() => {
                setTab(SUPERVISOR_TABS.QUEUE);
                setSelected(null);
              }}
            >
              主管佇列
            </button>
            <button
              type="button"
              className={tab === SUPERVISOR_TABS.ENDED ? 'is-active' : ''}
              onClick={() => {
                setTab(SUPERVISOR_TABS.ENDED);
                setSelected(null);
              }}
            >
              全量已結束
            </button>
            <button
              type="button"
              className={tab === SUPERVISOR_TABS.AUDIT ? 'is-active' : ''}
              onClick={() => {
                setTab(SUPERVISOR_TABS.AUDIT);
                setSelected(null);
                setSelectedAuditId(null);
              }}
            >
              Audit 日誌
            </button>
          </div>
          {tab === SUPERVISOR_TABS.AUDIT ? (
            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--color-border, #e5e7eb)' }}>
              <input
                className="cs-input"
                style={{ width: '100%' }}
                placeholder="依 action 過濾（如 ASSIGNMENT_CLAIM）"
                value={auditFilterAction}
                onChange={(e) => setAuditFilterAction(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') loadAuditLogs({ append: false });
                }}
              />
            </div>
          ) : null}
          <div className="cs-list">
            {tab === SUPERVISOR_TABS.QUEUE && queueItems.map((item) => (
              <div
                key={item.sessionId}
                className={`cs-list-item ${selected?.sessionId === item.sessionId ? 'is-selected' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => handleSelect(item, SUPERVISOR_TABS.QUEUE)}
                onKeyDown={(e) => e.key === 'Enter' && handleSelect(item, SUPERVISOR_TABS.QUEUE)}
              >
                <div className="cs-list-item__title">{sessionTitle(item)}</div>
                <div className="cs-list-item__meta">
                  {item.category || '—'} · 等待 {formatWaitSeconds(item.waitingDurationSeconds)}
                  {item.eligibleAgentCount != null ? ` · 合格 ${item.eligibleAgentCount}` : ''}
                </div>
              </div>
            ))}
            {tab === SUPERVISOR_TABS.ENDED && endedSessions.map((item) => (
              <div
                key={item.sessionId}
                className={`cs-list-item ${selected?.sessionId === item.sessionId ? 'is-selected' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => handleSelect(item, SUPERVISOR_TABS.ENDED)}
                onKeyDown={(e) => e.key === 'Enter' && handleSelect(item, SUPERVISOR_TABS.ENDED)}
              >
                <div className="cs-list-item__title">{sessionTitle(item)}</div>
                <div className="cs-list-item__meta">
                  {SESSION_STATUS_LABEL[item.status] || item.status || '—'}
                  {item.endReason ? ` · ${item.endReason}` : ''}
                  {item.agentAdminId != null ? ` · Agent #${item.agentAdminId}` : ''}
                </div>
                <div className="cs-list-item__preview">{formatEndedAt(item)}</div>
              </div>
            ))}
            {tab === SUPERVISOR_TABS.AUDIT && auditLogs.map((item) => (
              <div
                key={item.id}
                className={`cs-list-item ${selectedAuditId === item.id ? 'is-selected' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => handleSelectAudit(item)}
                onKeyDown={(e) => e.key === 'Enter' && handleSelectAudit(item)}
              >
                <div className="cs-list-item__title">
                  {AUDIT_ACTION_LABEL[item.action] || item.action || '—'}
                </div>
                <div className="cs-list-item__meta">
                  {formatAuditTime(item.createdAt)}
                  {item.sessionId != null ? ` · Session #${item.sessionId}` : ''}
                  {item.actorType ? ` · ${item.actorType}` : ''}
                  {item.actorId != null ? ` #${item.actorId}` : ''}
                </div>
                {item.detail || item.reason ? (
                  <div className="cs-list-item__preview">{item.detail || item.reason}</div>
                ) : null}
              </div>
            ))}
            {tab === SUPERVISOR_TABS.QUEUE && !queueItems.length ? <div className="cs-empty">佇列為空</div> : null}
            {tab === SUPERVISOR_TABS.ENDED && !endedSessions.length ? (
              <div className="cs-empty">尚無已結束 Session</div>
            ) : null}
            {tab === SUPERVISOR_TABS.AUDIT && !auditLogs.length ? (
              <div className="cs-empty">尚無 Audit 日誌（或尚無 TABLE 落庫）</div>
            ) : null}
            {tab === SUPERVISOR_TABS.AUDIT && auditHasMore ? (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ margin: 8 }}
                onClick={() => loadAuditLogs({ append: true, cursor: auditCursor })}
              >
                載入更多
              </button>
            ) : null}
          </div>
        </div>

        <div className="cs-panel" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <SessionTimeline
            messages={messages}
            error={msgError}
            hasMoreBefore={msgMeta.hasMoreBefore}
            onLoadOlder={() => {
              if (msgMeta.oldestSeq != null) {
                loadMessages(selected.sessionId, { beforeSeq: msgMeta.oldestSeq });
              }
            }}
          />
          <div className="cs-hint" style={{ borderTop: '1px solid var(--color-border, #e5e7eb)' }}>
            {tab === SUPERVISOR_TABS.AUDIT
              ? 'Audit 日誌（TABLE）；點選可回看對應 Session'
              : selectedEnded
                ? '已結束案件唯讀；可回看 Timeline／Audit'
                : '主管視圖預設唯讀；Watch 後仍不可發訊'}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
          <SessionSidePanel
            session={selected}
            readOnly
            watching={watching}
            onWatch={selectedEnded ? undefined : handleWatch}
            onUnwatch={selectedEnded ? undefined : handleUnwatch}
            onSoftAssign={selectedEnded ? undefined : () => setSoftAssignOpen(true)}
            onForceAssign={selectedEnded ? undefined : handleForceAssign}
            onForceRelease={selectedEnded ? undefined : handleForceRelease}
            onForceTransfer={selectedEnded ? undefined : handleForceTransfer}
            auditSummary={auditSummary}
            auditLoading={auditLoading}
          />
          <div className="cs-panel" style={{ flex: 1, minHeight: 180 }}>
            <div className="cs-panel__head">Agent Presence</div>
            <div className="cs-list">
              {agents.map((a) => (
                <div key={a.adminUserId || a.agentId} className="cs-agent-row">
                  <span>{a.displayName || a.username || a.adminUserId}</span>
                  <span className="cs-list-item__meta">
                    {a.presenceStatus || a.desiredStatus || '—'}
                    {' · '}
                    {a.currentActiveSessions ?? 0}/{a.maxConcurrentSessions ?? '—'}
                  </span>
                </div>
              ))}
              {!agents.length ? <div className="cs-empty">尚無 Agent</div> : null}
            </div>
          </div>
        </div>
      </div>
      <SoftAssignModal
        sessionId={selected?.sessionId}
        open={softAssignOpen}
        onClose={() => setSoftAssignOpen(false)}
        onAssigned={() => {
          setBanner('Soft Assign 已送出，等待客服接受');
          loadLists();
        }}
      />
    </div>
  );
}
